import React, { Suspense, lazy } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from './contexts/ThemeContext';

import SEOHead from './components/SEOHead';
import BlogRedirect from './components/BlogRedirect';
// The 404 page is in the main bundle: it is small, and every unknown URL gets it,
// so it should not wait on a second request.
import NotFoundPage from './pages/NotFoundPage';

// The prerendered pages: code-split, and loaded before React's first render when
// the app starts on one of them. See prerenderedRoutes.js.
import {
  BlogNavbar,
  BlogPage,
  BlogPostPage,
  TermsPage,
  PrivacyPage,
  ClientTermsPage,
  ClientPrivacyPage,
  ContractorTermsPage,
  ContractorPrivacyPage,
  ComparePage,
  PerksPage,
  PrepPage,
  GetPrepPage,
  PreLaunchPage,
} from './prerenderedRoutes';
import { COMPARE_PAGES } from './content/compare';

// Everything else is code-split too, and loads through Suspense.
//
// The two MUI-based SPAs are the only consumers of MUI, Emotion and Recharts, and
// importing them statically put that entire dependency tree into the main bundle,
// so every marketing and blog visitor downloaded the admin console before the
// page could render. These routes are session-gated and noindexed; nobody
// arriving from search needs them. The marketing site under /preview/* is most of
// the remaining code, and is noindexed until launch.
const AdminApp = lazy(() => import('./admin/AdminApp'));
const ParticipantApp = lazy(() => import('./portal/ParticipantApp'));
const MarketingSite = lazy(() => import('./marketing/MarketingSite'));
const InternationalWaitlistPage = lazy(() => import('./pages/InternationalWaitlistPage'));
const OfficialRulesPage = lazy(() => import('./pages/OfficialRulesPage'));
const PrivacyChoicesPage = lazy(() => import('./pages/PrivacyChoicesPage'));
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage'));
const MetaPixel = lazy(() => import('./components/MetaPixel'));
const CookieConsent = lazy(() => import('./components/CookieConsent'));
const ProductPreviewPage = lazy(() => import('./pages/ProductPreviewPage'));
const ClaimInvitePage = lazy(() => import('./pages/ClaimInvitePage'));
const AdminWaitlistPage = lazy(() => import('./pages/AdminWaitlistPage'));

function App() {
  return (
    <ThemeProvider>
      <div className="App transition-colors duration-300">
        <BrowserRouter>
          <SEOHead />
          {/* Meta Pixel (Privacy Policy 4.4) and the EEA/UK/CH cookie notice; rules in lib/adPrivacy.js. */}
          <Suspense fallback={null}>
            <MetaPixel />
            <CookieConsent />
          </Suspense>
          {/* A code-split page shows the page background while its chunk loads.
              The admin and portal keep their own dark fallback, below. */}
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <Routes>
              {/* Pre-Launch Page - Main landing page (no navigation) */}
              <Route path="/" element={<PreLaunchPage />} />
              <Route path="/beta" element={<PreLaunchPage />} />
              <Route path="/launch" element={<PreLaunchPage />} />
              <Route path="/waitlist" element={<PreLaunchPage />} />
              <Route path="/international" element={<InternationalWaitlistPage />} />

              {/* Legacy beta-invite CLAIM landing (operator-triggered campaign) */}
              <Route path="/claim" element={<ClaimInvitePage />} />

              {/* Official sweepstakes rules — linked from the landing page + the participant portal */}
              <Route path="/rules" element={<OfficialRulesPage />} />
              <Route path="/official-rules" element={<OfficialRulesPage />} />

              {/* Legal. Top-level and indexable on purpose: these must be reachable
                  from the live pre-launch site, not only from /preview/*, because
                  the waitlist is already collecting personal data today. */}
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/terms-of-service" element={<Navigate to="/terms" replace />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/privacy-choices" element={<PrivacyChoicesPage />} />
              <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
              <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />

              {/* Client-facing terms. Linked from branded client portals and
                  gallery links on domains we do not control, so this URL is a
                  published integration point: do not rename it. */}
              <Route path="/client-terms" element={<ClientTermsPage />} />
              <Route path="/gallery-terms" element={<Navigate to="/client-terms" replace />} />
              <Route path="/client-privacy" element={<ClientPrivacyPage />} />

              {/* Contractor terms and privacy notice. Linked by absolute URL from
                  the CRM's contractor sign-up, so these URLs are published
                  integration points: do not rename them. */}
              <Route path="/contractor-terms" element={<ContractorTermsPage />} />
              <Route path="/contractor-privacy" element={<ContractorPrivacyPage />} />

              {/* Founding Partners "Perks" page — PRIVATE during the quiet period:
                  noindex (page-level useRobotsMeta + X-Robots-Tag in vercel.json) and
                  NOT linked from any nav/footer/homepage/sitemap. Direct-URL access only. */}
              <Route path="/perks" element={<PerksPage />} />

              {/* Pixally Prep, the Mac app. Indexable and linked from the brand bar
                  (Ryan, 11 Oct 2026). */}
              <Route path="/prep" element={<PrepPage />} />

              {/* The page Prep's ads link to (Ryan, 14 Oct 2026): noindex
                  (page-level useRobotsMeta + X-Robots-Tag in vercel.json), and in
                  no nav, footer or sitemap. */}
              <Route path="/get-prep" element={<GetPrepPage />} />

              {/* Gated product preview for prospects, same posture as /perks:
                  noindex (page-level useRobotsMeta + X-Robots-Tag in vercel.json),
                  not linked from any nav/footer/sitemap, direct-URL only. The
                  password is client-side, so it is a curtain and not a lock;
                  protected surfaces are excluded from content/preview/modules.js
                  rather than relied upon to be hidden by it. */}
              <Route path="/first-look" element={<ProductPreviewPage />} />

              {/* Admin (secret token-protected) — legacy beta-signup viewer */}
              <Route path="/admin/waitlist/:token" element={<AdminWaitlistPage />} />

              {/* Admin Console (Prompt 11b) — Shared Pixally Shell, MUI scoped to /admin only */}
              <Route
                path="/admin/*"
                element={
                  <Suspense fallback={<div className="min-h-screen bg-pixally-dark-bg" />}>
                    <AdminApp />
                  </Suspense>
                }
              />

              {/* Participant Portal — same shared Pixally Shell + magic-link session, scoped to /portal only */}
              <Route
                path="/portal/*"
                element={
                  <Suspense fallback={<div className="min-h-screen bg-pixally-dark-bg" />}>
                    <ParticipantApp />
                  </Suspense>
                }
              />

              {/* Blog — top-level so SEO URLs (/blog/*) stay stable through launch.
                  Uses <BlogNavbar /> (logo + Join the Beta only) so organic search
                  traffic doesn't leak into the marketing site while it's gated. */}
              <Route
                path="/blog"
                element={
                  <>
                    <BlogNavbar />
                    <BlogPage />
                  </>
                }
              />
              <Route
                path="/blog/:slug"
                element={
                  <>
                    <BlogNavbar />
                    <BlogPostPage />
                  </>
                }
              />

              {/* Comparison pages: indexable at the root, like the blog, and
                  prerendered (content/compare/index.js). */}
              {COMPARE_PAGES.map((page) => (
                <Route
                  key={page.ROUTE}
                  path={page.ROUTE}
                  element={
                    <>
                      <BlogNavbar />
                      <ComparePage page={page} />
                    </>
                  }
                />
              ))}

              {/* Legacy /preview/blog/* → /blog/* (client-side 301-equivalent) */}
              <Route path="/preview/blog" element={<Navigate to="/blog" replace />} />
              <Route path="/preview/blog/:slug" element={<BlogRedirect />} />
            
              {/* Full Site Preview (hidden until launch) - Access at /preview */}
              <Route path="/preview/*" element={<MarketingSite />} />

              {/* Top-level catch-all: unknown paths get a real 404 UI + noindex.
                  Previously fell back to <PreLaunchPage />, which created a soft-200
                  where every URL indexed as the pre-launch page. */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
